import { createSelector, Selector } from '@reduxjs/toolkit'
import moment from 'moment'
import { isNil } from 'lodash-es'
import {
  AssetType,
  Collateral,
  CollateralDetail,
  CollateralDetails,
  CollateralStatus,
  Currency,
  EntityType,
  Instrument,
  Market,
  MarketType,
  RepoType
} from 'api'
import { GlobalState } from 'store'
import {
  CollateralHelper,
  collateralIsNotCompliant,
  collateralNotEnabled,
  currencyIsDifferent,
  getCollateralMarket,
  isAdmin,
  isCompatibleWithOriginalRepoType,
  isNotPartOfBasketCollateralIds,
  isOriginalCollateral,
  issuerOrganisationComplianceSuspended,
  tradingEntityCanTradeSector,
  ultimateParentOrgSanctionScreened,
  userCanTradeSector
} from 'model'
import {
  createAllEntitiesSelector,
  createAllEntityUpdatesSelector,
  createEntityGetterSelector,
  createEntitySelector
} from './entitySelectors'
import { createAuthorisedUserSelector } from './connectionSelectors'
import { showMaturedCollateralsSelector } from './gridSelectors'
import { compareInsensitive } from 'utils'
import { createUserNameByUserIdGetterSelector } from './userSelectors'
import { Locale, Translation } from 'locale'
import { productRecordSelector, productSectorRecordSelector } from './productSelectors'

const NULL_SELECTOR = () => null

export const allEnabledCollateralsSelector: Selector<GlobalState, Collateral[]> = createSelector(
  createAllEntitiesSelector(EntityType.COLLATERAL),
  allCollaterals => allCollaterals.filter(collateral => collateral.status === CollateralStatus.ENABLED)
)

export const allEnabledAndKnownCollateralsSelector: Selector<GlobalState, Collateral[]> = createSelector(
  allEnabledCollateralsSelector,
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  (allCollaterals, getProductSector) => allCollaterals.filter(collateral => !getProductSector(collateral.productSectorId)?.system)
)

export function createCollateralSelector<TProps>(
  isinSelector: Selector<{}, string | null | undefined, [TProps]>
): Selector<GlobalState, Collateral | null | undefined, [TProps]> {
  return createEntitySelector(isinSelector, EntityType.COLLATERAL)
}

export function createCollateralProductSectorIdSelector<TProps>(
  isinSelector: Selector<{}, string | null | undefined, [TProps]>
): Selector<GlobalState, string | null | undefined, [TProps]> {
  return createSelector([createCollateralSelector(isinSelector)], collateral => (collateral ? collateral.productSectorId : null))
}

export function createSubstitutableCollateralIdListSelector<TProps>(
  fundIdSelector: Selector<{}, string, [TProps]>,
  counterpartyIdSelector: Selector<{}, string, [TProps]>,
  parentDealBasketIdSelector: Selector<{}, string | null | undefined, [TProps]>,
  originalIsinSelector: Selector<{}, string, [TProps]>,
  originalRepoTypeSelector: Selector<{}, RepoType, [TProps]>,
  productIdSelector: Selector<{}, string | null, [TProps]>,
  currencySelector: Selector<{}, Currency, [TProps]>,
  marketTypeSelector: Selector<{}, MarketType | null, [TProps]>
): Selector<GlobalState, string[], [TProps]> {
  return createSelector(
    createAuthorisedUserSelector(),
    createCollateralsByMicCodeSelector(productIdSelector, currencySelector, marketTypeSelector),
    createEntitySelector(fundIdSelector, EntityType.TRADING_ENTITY),
    createEntitySelector(counterpartyIdSelector, EntityType.TRADING_ENTITY),
    parentDealBasketIdSelector,
    createEntitySelector(originalIsinSelector, EntityType.COLLATERAL),
    createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    originalRepoTypeSelector,
    marketTypeSelector,
    (
      authorisedUser,
      collateralsByMicCode,
      fund,
      counterparty,
      parentDealBasketId,
      originalCollateral,
      getBasket,
      getProductSector,
      originalRepoType,
      marketType
    ) =>
      collateralsByMicCode
        .filter(collateral => {
          if (
            isOriginalCollateral(collateral, originalCollateral) ||
            currencyIsDifferent(collateral, originalCollateral) ||
            collateralNotEnabled(collateral)
          ) {
            return false
          }

          if (parentDealBasketId && isNotPartOfBasketCollateralIds(parentDealBasketId, collateral.id, getBasket)) {
            return false
          }

          const hasUserProductRights = isAdmin(authorisedUser) || userCanTradeSector(collateral.productSectorId, authorisedUser, marketType)

          return (
            hasUserProductRights &&
            tradingEntityCanTradeSector(collateral.productSectorId, fund, marketType) &&
            tradingEntityCanTradeSector(collateral.productSectorId, counterparty, marketType) &&
            isCompatibleWithOriginalRepoType(getProductSector, collateral.productSectorId, originalRepoType)
          )
        })
        .sort((coll1, coll2) => compareInsensitive(CollateralHelper.getName(coll1), CollateralHelper.getName(coll2)))
        .map(collateral => collateral.id)
  )
}

export function createCollateralDetailsSelector<TProps>(
  marketSelector: Selector<{}, Market | null | undefined, [TProps]> = NULL_SELECTOR
): Selector<GlobalState, CollateralDetail[], [TProps]> {
  return createSelector(
    createAllEntityUpdatesSelector(EntityType.COLLATERAL),
    createEntityGetterSelector(EntityType.COLLATERAL_PRICE),
    createEntityGetterSelector(EntityType.PRODUCT),
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    createEntityGetterSelector(EntityType.INDEX),
    marketSelector,
    showMaturedCollateralsSelector,
    createUserNameByUserIdGetterSelector,
    (
      collateralUpdates,
      getCollateralPrice,
      getProduct,
      getProductSector,
      getIndex,
      market,
      showMaturedCollaterals,
      getUserNameFromUserId
    ) => {
      const collateralDetails = collateralUpdates
        .filter(
          update =>
            showMaturedCollaterals ||
            !update.entity.couponMaturityDate ||
            moment.utc(update.entity.couponMaturityDate).isSameOrAfter(moment.utc(), 'day')
        )
        .map(update => {
          const collateral = update.entity
          const productSector = getProductSector(update.entity.productSectorId)
          const product = productSector && getProduct(productSector.productId)
          const collateralPrice = getCollateralPrice(collateral.id)
          const index = collateral.collateralIndexDetails?.indexId ? getIndex(collateral.collateralIndexDetails.indexId) : null
          const availableEvents = update.availableEvents
          const lastUpdatedByUserName = getUserNameFromUserId(collateral.lastUpdatedByUser) ?? Locale.translate(Translation.SC_SYSTEM)
          return { collateral, collateralPrice, product, productSector, index, availableEvents, lastUpdatedByUserName }
        })

      return isNil(market) ? collateralDetails : collateralDetails.filter(collateralDetail => collateralDetail.product?.market === market)
    }
  )
}

export function createCollateralByCurrencySelector<TProps>(
  collateralBasketCurrencySelector: Selector<{}, string | null | undefined, [TProps]>
): Selector<GlobalState, Collateral[], [TProps]> {
  return createSelector(collateralBasketCurrencySelector, createAllEntitiesSelector(EntityType.COLLATERAL), (currency, allCollaterals) => {
    return allCollaterals.filter(collateral => collateral.currency === currency)
  })
}

export const createCollateralByIdGetterSelector: Selector<
  GlobalState,
  (collateralId: string) => Collateral | null
> = createSelector(createEntityGetterSelector(EntityType.COLLATERAL), getCollateralById => (collateralId: string) =>
  getCollateralById(collateralId)
)

export const createCollateralDetailsByIdGetterSelector: Selector<
  GlobalState,
  (collateralId: string) => CollateralDetails | null
> = createSelector(createEntityGetterSelector(EntityType.COLLATERAL_DETAILS), getCollateralDetailsById => (collateralId: string) =>
  getCollateralDetailsById(collateralId)
)

export const createCollateralByIsinOrIdGetterSelector: Selector<
  GlobalState,
  (collateralIsinOrId: string) => Collateral | null
> = createSelector(createAllEntitiesSelector(EntityType.COLLATERAL), collaterals => (collateralIsinOrId: string) =>
  collaterals.find(collateral => collateral.isin === collateralIsinOrId || collateral.id === collateralIsinOrId) || null
)

export const createCollateralByIsinGetterSelector: Selector<
  GlobalState,
  (isin: string) => Collateral | undefined
> = createSelector(createAllEntitiesSelector(EntityType.COLLATERAL), collaterals => (isin: string) =>
  collaterals.find(collateral => collateral.isin === isin)
)

export const instrumentsAreAllUSMarketGetterSelector = createSelector(
  [createEntityGetterSelector(EntityType.PRODUCT_SECTOR), createEntityGetterSelector(EntityType.PRODUCT)],
  (getProductSector, getProduct) => (instruments: Instrument[]) =>
    instruments.every(instrument => {
      const productSector = getProductSector(instrument.productSectorId)
      const market = productSector && getProduct(productSector.productId)?.market
      return market === Market.US
    })
)

export const createCollateralsByMicCodeSelector = <TProps>(
  productIdSelector: Selector<{}, string | null | undefined, [TProps]>,
  currencySelector: Selector<{}, Currency | null | undefined, [TProps]>,
  marketTypeSelector: Selector<{}, MarketType | null, [TProps]>
) =>
  createSelector(
    [
      productSectorRecordSelector,
      productRecordSelector,
      productIdSelector,
      createCollateralByCurrencySelector(currencySelector),
      marketTypeSelector
    ],
    (productSectors, products, productId, collateralsByCurrency, marketType) => {
      const micCodePropertyName = marketType === MarketType.PRIMARY ? 'micCode' : 'secondaryMicCode'

      const micCode = productId ? products?.[productId]?.[micCodePropertyName] : null
      const collateralsByCurrencyAndMicCode: Collateral[] = collateralsByCurrency.filter(c => {
        const candidateProductSectorId = productSectors?.[c.productSectorId]?.productId ?? ''

        return products?.[candidateProductSectorId]?.[micCodePropertyName] === micCode
      })

      return collateralsByCurrencyAndMicCode
    }
  )

export const isCollateralActiveSelector: Selector<GlobalState, (collateral: Collateral) => Boolean> = createSelector(
  createAllEntitiesSelector(EntityType.ISSUER_ORGANISATION),
  issuerOrganisations => {
    return (collateral: Collateral) => {
      const collateralMarket = getCollateralMarket(collateral)

      const collateralIsCorp = collateral.assetType === AssetType.CORP
      const collateralIsMort = collateral.assetType === AssetType.MORT_NON_AGENCY

      if ((collateralIsCorp || collateralIsMort) && collateralMarket === Market.EUROPE && collateralIsNotCompliant(collateral)) {
        return false
      }

      if (
        !ultimateParentOrgSanctionScreened(collateral, issuerOrganisations) ||
        issuerOrganisationComplianceSuspended(collateral, issuerOrganisations)
      ) {
        return false
      }

      return true
    }
  }
)
